/**
 * Passwort zuruecksetzen, Schritt 1: Mail anfordern.
 *
 * WARUM DIE ANTWORT IMMER GLEICH AUSSIEHT: Wuerde die Seite „diese Adresse
 * kennen wir nicht" sagen, waere sie ein Verzeichnis, mit dem sich pruefen
 * laesst, wer Kunde ist. Der Text sagt deshalb, dass eine Mail unterwegs ist,
 * falls es das Konto gibt — und genau das tut Supabase auch.
 */

import { useState, type FormEvent } from 'react';
import { Feld, Hinweis, Knopf, Rahmen, Verweis } from '../ui';
import { fehlertext, supabase } from '../supabase';
import { MAIL_ABSENDER, PFAD_PASSWORT_NEU, redirectTo } from '../config';
import { t } from '../sprache';
import { useTurnstile } from '../turnstile';

export default function PasswortVergessen() {
  const [email, setEmail] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [fertig, setFertig] = useState(false);
  const pruefung = useTurnstile('reset');

  function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler('');
    pruefung.ausfuehren(anfordern);
  }

  async function anfordern(captchaToken: string) {
    setLaedt(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: redirectTo(PFAD_PASSWORT_NEU),
      captchaToken,
    });
    setLaedt(false);
    // Ein Rate-Limit ist der eine Fall, den der Nutzer wissen muss: sonst
    // drueckt er noch dreimal und wundert sich ueber die ausbleibende Mail.
    if (error) {
      setFehler(fehlertext(error));
      return;
    }
    setFertig(true);
  }

  if (fertig) {
    return (
      <Rahmen
        titel={t('Mail ist unterwegs', 'Email is on its way')}
        unter={t(
          <>Falls es ein Konto für <strong className="text-text">{email.trim()}</strong> gibt, liegt gleich ein Link im Postfach. Er gilt eine Stunde.</>,
          <>If there is an account for <strong className="text-text">{email.trim()}</strong>, a link will be in your inbox shortly. It is valid for one hour.</>,
        )}
        fuss={<Verweis to="/login">{t('Zurück zur Anmeldung', 'Back to sign in')}</Verweis>}
      >
        <Hinweis>{t(
          <>Nichts angekommen? Sieh im Spam nach. Absender: {MAIL_ABSENDER.name} &lt;{MAIL_ABSENDER.adresse}&gt;.</>,
          <>Nothing arrived? Check your spam folder. Sender: {MAIL_ABSENDER.name} &lt;{MAIL_ABSENDER.adresse}&gt;.</>,
        )}</Hinweis>
      </Rahmen>
    );
  }

  return (
    <Rahmen
      titel={t('Passwort zurücksetzen', 'Reset password')}
      unter={t('Wir schicken dir einen Link, mit dem du ein neues Passwort setzen kannst.', 'We send you a link to set a new password.')}
      fuss={<Verweis to="/login">{t('Zurück zur Anmeldung', 'Back to sign in')}</Verweis>}
    >
      <form onSubmit={absenden}>
        {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
        <Feld
          label={t('E-Mail', 'Email')} type="email" name="email" autoComplete="email" required autoFocus
          value={email} onChange={e => setEmail(e.target.value)} disabled={laedt}
        />
        {pruefung.feld}
        <Knopf type="submit" laedt={laedt || pruefung.wartet}>{t('Link anfordern', 'Request link')}</Knopf>
      </form>
    </Rahmen>
  );
}
